import { useCallback } from "react";
import useSWR from "swr";
import {
  getDropdownOptions,
  addDropdownOption,
  editDropdownOption,
  deleteDropdownOption,
} from "services/dropdownService";
import { toast } from "sonner";

/**
 * A custom hook to manage states and CRUD handlers for generic dropdowns based on an enum value.
 * @param {number} enumValue - The dropdown enum type value
 * @param {boolean} enabled - Whether fetching is enabled (default: true)
 * @returns {Object} An object containing options, loading state, and CRUD handlers
 */
export default function useDropdown(enumValue, enabled = true) {
  const key = (enabled && enumValue !== undefined && enumValue !== null)
    ? ["dropdown-options", enumValue]
    : null;

  const { data: options = [], isLoading, mutate } = useSWR(
    key,
    async () => {
      const res = await getDropdownOptions(enumValue);
      if (res.success) {
        return (res.data || []).map((opt) => ({
          value: opt.id || opt.Id,
          label: opt.optionName || opt.OptionName,
        }));
      }
      throw new Error(res.error || "Failed to load options");
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000,
    }
  );

  const fetchOptions = useCallback(async () => {
    return mutate();
  }, [mutate]);

  const handleCreateOption = useCallback(
    async (name) => {
      try {
        const res = await addDropdownOption(enumValue, name);
        if (res.success) {
          toast.success("Option added successfully");
          await fetchOptions();
          return res;
        } else {
          toast.error(res.error || "Failed to add option");
        }
      } catch (err) {
        console.error("Error creating option:", err);
        toast.error("Failed to add option");
      }
      return { success: false };
    },
    [enumValue, fetchOptions]
  );

  const handleEditOption = useCallback(
    async (optionId, currentLabel, newText) => {
      try {
        const res = await editDropdownOption(optionId, newText);
        if (res.success) {
          toast.success("Option updated successfully");
          await fetchOptions();
          return true;
        } else {
          toast.error(res.error || "Failed to update option");
        }
      } catch (err) {
        console.error("Error editing option:", err);
        toast.error("Failed to update option");
      }
      return false;
    },
    [fetchOptions]
  );

  const handleDeleteOption = useCallback(
    async (optionId, label, skipConfirm = false) => {
      if (!skipConfirm) {
        if (!confirm) return false;
      }

      try {
        const res = await deleteDropdownOption(optionId);
        if (res.success) {
          toast.success("Option deleted successfully");
          await fetchOptions();
          return true;
        } else {
          toast.error(res.error || "Failed to delete option");
        }
      } catch (err) {
        console.error("Error deleting option:", err);
        toast.error("Failed to delete option");
      }
      return false;
    },
    [fetchOptions]
  );

  return {
    options,
    isLoading,
    refresh: fetchOptions,
    onCreateOption: handleCreateOption,
    onEditOption: handleEditOption,
    onDeleteOption: handleDeleteOption,
  };
}
